import { useState } from 'react';

import { Banner, Box, H1, Text, VStack } from '@betterment/samba-react';
import { FormikHelpers } from 'formik';
import { useAtom } from 'jotai';

import { flashAtom } from '~/b4b/apps/FlashApp';
import { ErrorMap, GraphQLMutationForm } from '~/b4b/components/graphql-mutation-form/GraphQLMutationForm';
import { useCurrentEmployeeId } from '~/b4b/utils/currentEmployee';
import { useCurrentEmployerId } from '~/b4b/utils/currentEmployer';
import { payrollContributionRateToString } from '~/b4b/utils/formatHelpers';
import { navigate } from '~/b4b/utils/pathHelpers';
import { urlBuilder } from '~/b4b/utils/urlBuilder';
import { DisplayableError, LegalAccountPayrollContributionRatesInput, PayrollContributionRate } from '~/graphql/schema';

import { ContributionRatesForm } from './ContributionRatesForm';
import { ContributionRateEditorValue, ContributionRateFormValues } from './types';
import { editorValueToRateInput } from './utils';
import { formValidationSchema } from './yupSchema';

import {
  UpdateEmployeeContributionRatesDocument,
  UpdateEmployeeContributionRatesMutationVariables,
} from '../graphql/mutation.updateEmployeeContributionRates.generated';
import { EmployeeContributionRatesQuery } from '../graphql/query.employeeContributionRates.generated';

function toEditorValue(rate: PayrollContributionRate): ContributionRateEditorValue {
  return {
    text: payrollContributionRateToString(rate),
    type: rate.__typename,
  };
}

function toFormValues(queryResponse: EmployeeContributionRatesQuery): ContributionRateFormValues {
  const tradAccount = queryResponse.employer.employee.traditional401kLegalAccount;
  const rothAccount = queryResponse.employer.employee.roth401kLegalAccount;

  return {
    base: {
      traditional: toEditorValue(tradAccount.baseContributionRate),
      roth: rothAccount ? toEditorValue(rothAccount.baseContributionRate) : undefined,
    },
    bonus: tradAccount.bonusContributionRate
      ? {
          traditional: toEditorValue(tradAccount.bonusContributionRate),
          roth: rothAccount ? toEditorValue(rothAccount.bonusContributionRate!) : undefined,
        }
      : undefined,
  };
}

function createDefaultErrorHandler(formikFieldName: string) {
  return async (error: DisplayableError, helpers: FormikHelpers<ContributionRateFormValues>) => {
    await helpers.setFieldTouched(formikFieldName);
    helpers.setFieldError(formikFieldName, error.message);
  };
}

function buildErrorHandlers(): ErrorMap<ContributionRateFormValues> {
  return {
    traditional_base_rate: createDefaultErrorHandler('base.traditional.text'),
    traditional_bonus_rate: createDefaultErrorHandler('bonus.traditional.text'),
    roth_base_rate: createDefaultErrorHandler('base.roth.text'),
    roth_bonus_rate: createDefaultErrorHandler('bonus.roth.text'),
  };
}

export const LoadedViewContent = ({
  employeeName,
  queryResponse,
  isEnrolledInAutoEscalation,
  hasOutboundPayrollIntegration,
  payrollProviderName,
}: {
  employeeName: string;
  isEnrolledInAutoEscalation: boolean;
  hasOutboundPayrollIntegration: boolean;
  payrollProviderName: string;
  queryResponse: EmployeeContributionRatesQuery;
}) => {
  const employerId = useCurrentEmployerId();
  const employeeId = useCurrentEmployeeId();
  const initialValues = toFormValues(queryResponse);
  const [, setStoredFlash] = useAtom(flashAtom);

  const [hasSubmittedSuccessfully, setHasSubmittedSuccessfully] = useState<boolean>(false);

  function prepareValues(values: ContributionRateFormValues): UpdateEmployeeContributionRatesMutationVariables {
    const tradAccount = queryResponse.employer.employee.traditional401kLegalAccount;
    const rothAccount = queryResponse.employer.employee.roth401kLegalAccount;
    const rates: LegalAccountPayrollContributionRatesInput[] = [];

    rates.push({
      legalAccountId: tradAccount.id,
      baseRate: editorValueToRateInput(values.base.traditional),
      bonusRate: values.bonus?.traditional ? editorValueToRateInput(values.bonus?.traditional) : null,
    });

    if (rothAccount) {
      rates.push({
        legalAccountId: rothAccount.id,
        baseRate: editorValueToRateInput(values.base.roth!),
        bonusRate: values.bonus?.roth ? editorValueToRateInput(values.bonus?.roth) : null,
      });
    }

    return {
      input: { employerId, employeeId, legalAccountPayrollContributionRates: rates },
    };
  }

  function handleSuccess() {
    // Keep the form in the submitting state as we navigate away.
    setHasSubmittedSuccessfully(true);
    setStoredFlash({
      message: 'Success! Contributions updated.',
      variant: 'success',
    });
    navigate(urlBuilder.employee({ employeeId }));
  }

  return (
    <>
      <H1 variant="title200">{employeeName}</H1>
      <VStack css={{ width: 'size.full', marginTop: 'space.100', marginBottom: 'space.400', gap: 'space.400' }}>
        <Box>
          <Text as="p">Employees will be notified via email of any changes made here to their contributions.</Text>
          {!!initialValues.bonus && (
            <Text as="p">Not all employees earn bonus pay; bonuses are at the employer’s discretion.</Text>
          )}
        </Box>
        {isEnrolledInAutoEscalation && (
          <Banner variant="info">
            Changing contribution rates will turn off auto-escalation for the upcoming year.
          </Banner>
        )}
        {!hasOutboundPayrollIntegration && (
          <Banner variant="warning">
            You must also reflect this employee’s contribution rates in {payrollProviderName || 'your payroll provider'}{' '}
            for changes to take effect.
          </Banner>
        )}
      </VStack>
      <Box css={{ width: 'size.full' }}>
        <GraphQLMutationForm
          initialValues={initialValues}
          mutation={UpdateEmployeeContributionRatesDocument}
          validationSchema={formValidationSchema}
          errorHandlers={buildErrorHandlers}
          prepareValues={prepareValues}
          onSuccess={handleSuccess}
        >
          {({ isSubmitting }) => (
            <ContributionRatesForm
              hasRothAccount={!!initialValues.base.roth}
              hasBonusRates={!!initialValues.bonus}
              isSubmitting={isSubmitting || hasSubmittedSuccessfully}
            />
          )}
        </GraphQLMutationForm>
      </Box>
    </>
  );
};
